import { DrawingModel, type Handle } from "../DrawingModel";
import type { Chart } from "../../chart/Chart";
import { distToSegment, withAlpha } from "../geometry";

const TOL = 6;

const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const FIB_EXT_LEVELS = [0, 0.618, 1, 1.618, 2.618, 3.618, 4.236];

const LEVEL_COLORS: Record<number, string> = {
  0: "#787b86",
  0.236: "#ef5350",
  0.382: "#ff9800",
  0.5: "#4caf50",
  0.618: "#26a69a",
  0.786: "#26c6da",
  1: "#787b86",
  1.618: "#2962ff",
  2.618: "#9c27b0",
  3.618: "#e040fb",
  4.236: "#f06292",
};

export class FibRetracement extends DrawingModel {
  type = "fibretr";
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b] = this.anchors;
    const xa = this.X(a, chart),
      xb = this.X(b, chart);
    const left = Math.min(xa, xb);
    const right = chart.plotWidth;
    ctx.font = "10px -apple-system, system-ui, sans-serif";
    ctx.textBaseline = "middle";
    let prevY = NaN;
    for (const lvl of FIB_LEVELS) {
      const price = a.price + (b.price - a.price) * lvl;
      const y = chart.viewport.yOfPrice(price);
      const col = LEVEL_COLORS[lvl] ?? this.style.color;
      if (this.style.fillAlpha > 0 && isFinite(prevY)) {
        ctx.fillStyle = withAlpha(col, this.style.fillAlpha);
        ctx.fillRect(left, Math.min(prevY, y), right - left, Math.abs(y - prevY));
      }
      ctx.strokeStyle = col;
      ctx.lineWidth = this.style.width;
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
      ctx.stroke();
      ctx.fillStyle = col;
      ctx.textAlign = "left";
      ctx.fillText(`${lvl.toFixed(3)} (${price.toFixed(chart.priceDecimals)})`, left + 4, y - 6);
      prevY = y;
    }
    // trend line connecting anchors
    ctx.strokeStyle = this.style.color;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(xa, this.Y(a, chart));
    ctx.lineTo(xb, this.Y(b, chart));
    ctx.stroke();
    ctx.setLineDash([]);
  }
  handles(chart: Chart): Handle[] {
    return this.anchors.map((a, i) => ({ x: this.X(a, chart), y: this.Y(a, chart), kind: i }));
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b] = this.anchors;
    const left = Math.min(this.X(a, chart), this.X(b, chart));
    const right = chart.plotWidth;
    if (px < left - TOL || px > right) return false;
    for (const lvl of FIB_LEVELS) {
      const price = a.price + (b.price - a.price) * lvl;
      if (Math.abs(py - chart.viewport.yOfPrice(price)) <= TOL) return true;
    }
    return false;
  }
}

// Fibonacci extension: anchors A,B define the move, C the retrace origin.
export class FibExtension extends DrawingModel {
  type = "fibext";
  get points() {
    return 2;
  }
  render(ctx: CanvasRenderingContext2D, chart: Chart) {
    const [a, b, c] = this.anchors;
    const base = c ?? b;
    const xC = this.X(base, chart);
    const right = chart.plotWidth;
    ctx.font = "10px -apple-system, system-ui, sans-serif";
    ctx.textBaseline = "middle";
    // connecting lines A-B-C
    this.applyStroke(ctx);
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.moveTo(this.X(a, chart), this.Y(a, chart));
    ctx.lineTo(this.X(b, chart), this.Y(b, chart));
    if (c) ctx.lineTo(this.X(c, chart), this.Y(c, chart));
    ctx.stroke();
    ctx.setLineDash([]);
    for (const lvl of FIB_EXT_LEVELS) {
      const price = base.price + (b.price - a.price) * lvl;
      const y = chart.viewport.yOfPrice(price);
      const col = LEVEL_COLORS[lvl] ?? this.style.color;
      ctx.strokeStyle = col;
      ctx.lineWidth = this.style.width;
      ctx.beginPath();
      ctx.moveTo(xC, y);
      ctx.lineTo(right, y);
      ctx.stroke();
      ctx.fillStyle = col;
      ctx.textAlign = "left";
      ctx.fillText(`${lvl.toFixed(3)} (${price.toFixed(chart.priceDecimals)})`, xC + 4, y - 6);
    }
  }
  handles(chart: Chart): Handle[] {
    return this.anchors.map((a, i) => ({ x: this.X(a, chart), y: this.Y(a, chart), kind: i }));
  }
  hitTest(px: number, py: number, chart: Chart) {
    const [a, b, c] = this.anchors;
    const base = c ?? b;
    const xC = this.X(base, chart);
    if (px < xC - TOL) return false;
    for (const lvl of FIB_EXT_LEVELS) {
      const price = base.price + (b.price - a.price) * lvl;
      if (Math.abs(py - chart.viewport.yOfPrice(price)) <= TOL) return true;
    }
    return false;
  }
}
